import { useState } from 'react'
import { useNavigate } from 'react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Copy } from 'lucide-react'

import { FieldLabel } from '@/components/FieldLabel'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { DataExportsApi, type DataExport } from '@/services/networks/dataExportsApi'

interface CopyDataExportDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  dataExport: DataExport
}

/** Creates a new data export with another's settings, secrets included. */
export function CopyDataExportDialog({ open, onOpenChange, dataExport }: CopyDataExportDialogProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [name, setName] = useState(t('copyDataExport.copyName', { name: dataExport.name }))
  const [description, setDescription] = useState(dataExport.description)
  const [openCopy, setOpenCopy] = useState(true)

  const mutation = useMutation({
    mutationFn: () =>
      DataExportsApi.copy(
        dataExport.id,
        crypto.randomUUID().replace(/-/g, '').slice(0, 16),
        name.trim(),
        description
      ),
    onSuccess: (copy) => {
      queryClient.invalidateQueries({ queryKey: ['data-exports'] })
      toast.success(t('copyDataExport.copied'), {
        description: t('copyDataExport.copiedDetail', { name: copy.name }),
        duration: 3000,
      })
      onOpenChange(false)
      if (openCopy) navigate(`/admin/data-exports/${copy.id}`)
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : t('copyDataExport.failed'), {
        description: apiErrorDescription(error),
        duration: 5000,
      })
    },
  })

  const valid = !!name.trim()

  return (
    <Dialog open={open} onOpenChange={(next) => !mutation.isPending && onOpenChange(next)}>
      <DialogContent>
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (valid) mutation.mutate()
          }}
        >
          <DialogHeader>
            <DialogTitle>{t('copyDataExport.title')}</DialogTitle>
            <DialogDescription>
              {t('copyDataExport.description', { name: dataExport.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-data-export-name">{t('common.name')}</FieldLabel>
            <Input
              id="copy-data-export-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <FieldLabel htmlFor="copy-data-export-description">{t('common.description')}</FieldLabel>
            <Input
              id="copy-data-export-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="copy-data-export-open"
              checked={openCopy}
              onCheckedChange={(v) => setOpenCopy(v === true)}
            />
            <Label htmlFor="copy-data-export-open" className="font-normal">
              {t('copyDataExport.openCopy')}
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={!valid}>
              <Copy className="size-4" />
              {t('copyDataExport.copy')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
